import React, { useState, useEffect } from 'react';
import { FaCrown, FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../common/AppHeader';
import BottomNav from '../common/BottomNav';
import Toast from '../common/Toast';
import ButtonSpinner from '../common/ButtonSpinner';
import { useTranslation } from "react-i18next";
import {
  getMembershipStatus,
  activateFreeMembership,
  getCachedMembershipStatus,
  cacheMembershipStatus,
} from '../../services/coreService';
import styles from '../css/Membership.module.css';

export default function Membership() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasMembership, setHasMembership] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showToast, setShowToast] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const spId = user.spId || "";

  useEffect(() => {
    const cachedMembership = getCachedMembershipStatus(spId);
    if (cachedMembership) {
      setHasMembership(
        !!(cachedMembership.responseCode === 200 && cachedMembership.responseData)
      );
      setLoading(false);
    }

    checkMembershipStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spId]);

  const checkMembershipStatus = async () => {
    try {
      if (!spId) {
        setLoading(false);
        return;
      }

      const membershipStatus = await getMembershipStatus(spId);
      console.log('Membership status check:', membershipStatus);
      
      if (membershipStatus && membershipStatus.responseCode === 200 && membershipStatus.responseData) {
        setHasMembership(true);
      } else {
        setHasMembership(false);
      }

      if (membershipStatus) {
        cacheMembershipStatus(spId, membershipStatus);
      }
    } catch (error) {
      console.error('Error checking membership:', error);
      setHasMembership(false);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateMembership = async () => {
    try {
      setActivating(true);
      
      if (!spId) {
        setMessage(t("Membership.user_not_found", "User not found. Please login again."));
        setMessageType("error");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
        setActivating(false);
        return;
      }

      const result = await activateFreeMembership(spId);
      
      if (result.success) {
        setMessage(t("Membership.activation_success", "Membership activated successfully!"));
        setMessageType("success");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setHasMembership(true);
        cacheMembershipStatus(spId, {
          responseCode: 200,
          responseMessage: 'Membership active',
          responseData: true,
        });
        
        // Navigate to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setMessage(t("Membership.activation_failed", "Failed to activate membership. Please try again."));
        setMessageType("error");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
      }
    } catch (error) {
      console.error('Error activating membership:', error);
      setMessage(t("Membership.activation_error", "An error occurred. Please try again."));
      setMessageType("error");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } finally {
      setActivating(false);
    }
  };

  const features = [
    { key: 'unlimited_listings', icon: '📋', text: t("Membership.unlimited_listings", "Unlimited service listings") },
    { key: 'service_management', icon: '⚙️', text: t("Membership.service_management", "Easy service management") },
    { key: 'real_time_notifications', icon: '🔔', text: t("Membership.real_time_notifications", "Real-time notifications") },
    { key: 'analytics_dashboard', icon: '📊', text: t("Membership.analytics_dashboard", "Analytics & insights") },
    { key: 'customer_support', icon: '💬', text: t("Membership.customer_support", "24/7 customer support") },
    { key: 'multi_language', icon: '🌍', text: t("Membership.multi_language", "Multi-language support") },
  ];



  return (
    <div className={styles.pageContainer}>
      <AppHeader title={t("Membership.membership", "Membership")} showBack={true} />

      <div className={styles.contentWrapper}>

        {/* Hero */}
        <div className={styles.heroCard}>
          <FaCrown className={styles.heroIcon} />
          <div className={styles.heroTitle}>Bhada24 Membership</div>
          <div className={styles.heroSubtitle}>
            {t("Membership.hero_sub", "Start free. Grow big. Only ₹799/year after your first year.")}
          </div>
        </div>

        {/* Offer Banner */}
        <div className={styles.offerBanner}>
          <span className={styles.offerEmoji}>🎉</span>
          <div className={styles.offerText}>
            <strong>{t("Membership.offer_title", "Launch Offer — 1 Year FREE!")}</strong>
            <span>{t("Membership.offer_sub", "Get full access free for your first year. Renew at just ₹799/year after that.")}</span>
          </div>
        </div>

        {/* Free Plan Card */}
        <div className={`${styles.planCard} ${hasMembership ? styles.planCardActive : ''}`}>
          <div className={styles.planHeader}>
            <div className={styles.planLabelRow}>
              <span className={styles.planBadgeFree}>FREE</span>
              <span className={styles.planName}>{t("Membership.free_plan", "1 Year Free Plan")}</span>
            </div>
            {hasMembership && (
              <div className={styles.activeBadge}>
                <span className={styles.activeDot} />
                {t("Membership.active", "ACTIVE")}
              </div>
            )}
          </div>

          <div className={styles.priceRow}>
            <span className={styles.priceMain}>₹0</span>
            <span className={styles.priceSub}>/ {t("Membership.first_year", "1st year")}</span>
          </div>
          <div className={styles.renewalNote}>
            {t("Membership.renewal_note", "Then ₹799/year — less than ₹67/month")}
          </div>

          <div className={styles.divider} />

          <div className={styles.featuresList}>
            {features.map((f, i) => (
              <div key={i} className={styles.featureItem}>
                <span className={styles.featureEmoji}>{f.icon}</span>
                <span className={styles.featureText}>{f.text}</span>
              </div>
            ))}
          </div>

          {loading ? (
            <button className={styles.planButton} disabled>
              <ButtonSpinner size={16} color="#667eea" type="dots" />
            </button>
          ) : hasMembership ? (
            <button className={`${styles.planButton} ${styles.planButtonActive}`} disabled>
              {t("Membership.current_plan", "Current Plan")}
            </button>
          ) : (
            <button
              className={styles.planButton}
              onClick={handleActivateMembership}
              disabled={activating}
            >
              {activating ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <ButtonSpinner size={16} color="#fff" type="dots" />
                  <span>{t("Membership.activating", "Activating...")}</span>
                </span>
              ) : t("Membership.activate_now", "Claim Free Year Now")}
            </button>
          )}
        </div>

        {/* Info Note */}
        <div className={styles.infoCard}>
          <div className={styles.infoTitle}>
            <FaInfoCircle color="#667eea" size={13} />
            <span>{t("Membership.why_free", "Why free for 1 year?")}</span>
          </div>
          <div className={styles.infoText}>
            {t("Membership.free_info", "We want you to experience the full power of Bhada24 before committing. After your free year, continue growing your business at just ₹799/year — the best investment for your service business.")}
          </div>
        </div>

      </div>

      {showToast && message && (
        <Toast
          message={message}
          success={messageType === "success"}
          onClose={() => {
            setShowToast(false);
            setMessage("");
          }}
        />
      )}

      <BottomNav />
    </div>
  );
}